import { useCallback, useMemo, useState } from 'react';

const useStep = (maxStep: number) => {
  const [currentStep, setCurrentStep] = useState(0);

  const canNextStep = useMemo(() => currentStep < maxStep - 1, [currentStep, maxStep]);
  const canPrevStep = useMemo(() => currentStep - 1 >= 0, [currentStep]);

  const nextStep = useCallback(() => {
    if (canNextStep) {
      setCurrentStep((step) => step + 1);
    }
  }, [canNextStep]);

  const previousStep = useCallback(() => {
    if (canPrevStep) {
      setCurrentStep((step) => step - 1);
    }
  }, [canPrevStep]);

  const resetSteps = useCallback(() => {
    setCurrentStep(0);
  }, []);

  const setStep = useCallback(
    (step: unknown) => {
      const newStep = step instanceof Function ? step(currentStep) : step;
      if (newStep >= 0 && newStep <= maxStep) {
        setCurrentStep(newStep);
        return;
      }
      throw new Error('Step not valid');
    },
    [maxStep, currentStep]
  );

  return { currentStep, nextStep, previousStep, canNextStep, canPrevStep, resetSteps, setStep };
};

export default useStep;
